'use client';

import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@unreserved/ui/components/dialog';
import { Button } from '@unreserved/ui/components/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DismissCircleIcon } from '@/app/_components/icons/dismiss-circle';

export interface FooterButtonsConfig {
  onCancel: () => void;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  confirmVariant?: 'default' | 'destructive';
  confirmButtonClassName?: string;
  cancelButtonClassName?: string;
}

export interface AdminDialogWrapperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: FooterButtonsConfig;
  className?: string;
  onClose?: () => void;
  contentPadding?: boolean;
  titleClassName?: string;
  useCircleClose?: boolean;
  contentClassName?: string;
  footerClassName?: string;
}

export function AdminDialogWrapper({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  onClose,
  contentPadding = true,
  titleClassName,
  useCircleClose = false,
  contentClassName,
  footerClassName,
}: AdminDialogWrapperProps) {
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'bg-white rounded-md border border-border-100 p-0 !max-w-md shadow-[0px_3px_10px_0px_rgba(38,38,38,0.2)]',
          'w-full max-h-[90vh] flex flex-col',
          className,
        )}
        showCloseButton={false}
      >
        <DialogHeader className="relative px-6 pt-6 pb-0 shrink-0">
          <DialogTitle
            className={cn(
              'text-lg font-semibold text-typography-900 pr-6 leading-[100%] block',
              titleClassName,
            )}
          >
            {title}
          </DialogTitle>
          {description && (
            <p className="text-sm text-typography-700 mt-1">{description}</p>
          )}
          {useCircleClose ? (
            <button
              onClick={handleClose}
              className="cursor-pointer absolute top-5 right-5 bg-transparent border-none p-0"
              aria-label="Close"
            >
              <DismissCircleIcon className="h-5 w-5" />
            </button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="cursor-pointer absolute top-6 right-6 h-[18px] w-[18px] !bg-transparent hover:!bg-transparent !text-typography-400 hover:!text-typography-900 focus-visible:!ring-0 focus-visible:!outline-none p-0"
              aria-label="Close"
            >
              <X className="h-[18px] w-[18px]" />
            </Button>
          )}
        </DialogHeader>

        <div
          className={cn(
            'px-6 flex-1 min-h-0 overflow-y-auto',
            contentPadding ? 'py-6' : 'pt-2 pb-0',
            contentClassName,
          )}
        >
          {children}
        </div>

        {footer && (
          <DialogFooter
            className={cn(
              'flex !justify-end sm:!justify-end pt-0 px-6 pb-6 shrink-0',
              footerClassName ?? 'gap-2',
            )}
          >
            <Button
              variant={
                typeof footer.onConfirm === 'function' ? 'outline' : 'default'
              }
              type="button"
              onClick={footer.onCancel}
              className={cn(
                typeof footer.onConfirm === 'function'
                  ? 'cursor-pointer h-9 border !border-typography-950 !bg-white text-typography-950 hover:!bg-white hover:!text-typography-950 px-4 rounded-sm text-sm font-medium'
                  : 'cursor-pointer h-9 px-4 rounded-sm text-sm font-medium !bg-primary-700 text-white hover:!bg-primary-700 hover:!text-white',
                footer.cancelButtonClassName,
              )}
            >
              {footer.cancelLabel ?? 'Cancel'}
            </Button>
            {typeof footer.onConfirm === 'function' && (
              <Button
                type="button"
                onClick={footer.onConfirm}
                disabled={footer.disabled}
                className={cn(
                  'cursor-pointer h-9 px-4 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium',
                  footer.confirmVariant === 'destructive'
                    ? 'bg-error-600 text-white hover:bg-error-600 hover:text-white'
                    : '!bg-primary-700 text-white hover:!bg-primary-700 hover:!text-white',
                  footer.confirmButtonClassName,
                )}
              >
                {footer.confirmLabel ?? 'Save & Confirm'}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
